import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Copy,
  CheckCircle,
  MessageCircle,
  RefreshCw,
  Ban,
  Loader2,
  PlusCircle,
  XCircle,
  RotateCcw,
  RefreshCcw,
  UserPlus,
  History,
  KeyRound
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { ApiService } from '@/services/api'
import type { OrdoreMiniLicense, OrdoreMiniAuditLogEntry } from '@/services/api'
import { toast } from 'sonner'

interface OrdoreMiniLicenseDetailPanelProps {
  license: OrdoreMiniLicense | null
  open: boolean
  onClose: () => void
  onRenew: (license: OrdoreMiniLicense) => void
  onRevoke: (license: OrdoreMiniLicense) => void
}

const ACTION_ICON: Record<string, React.ElementType> = {
  GENERATED: PlusCircle,
  RENEWED: RefreshCcw,
  REVOKED: Ban,
  REACTIVATED: RotateCcw,
  SYNCED: RefreshCw,
  SIGNED_UP: UserPlus,
  PASSWORD_RESET: KeyRound
}

const ACTION_LABEL: Record<string, string> = {
  GENERATED: 'License generated',
  RENEWED: 'License renewed',
  REVOKED: 'License revoked',
  REACTIVATED: 'License reactivated',
  SYNCED: 'Status synced',
  SIGNED_UP: 'Owner signed up',
  PASSWORD_RESET: 'Password reset'
}

function statusBadge(status: OrdoreMiniLicense['status']) {
  switch (status) {
    case 'ACTIVE':
      return <Badge className="bg-green-100 text-green-800 border-green-200">Active</Badge>
    case 'GRACE':
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Grace Period</Badge>
    case 'EXPIRED':
      return <Badge className="bg-red-100 text-red-800 border-red-200">Expired</Badge>
    case 'REVOKED':
      return <Badge className="bg-gray-100 text-gray-600 border-gray-200">Revoked</Badge>
  }
}

export default function OrdoreMiniLicenseDetailPanel({ license, open, onClose, onRenew, onRevoke }: OrdoreMiniLicenseDetailPanelProps) {
  const [auditLog, setAuditLog] = useState<OrdoreMiniAuditLogEntry[]>([])
  const [isLoadingAudit, setIsLoadingAudit] = useState(false)
  const [copied, setCopied] = useState(false)
  const [isResettingPassword, setIsResettingPassword] = useState(false)
  const [tempPassword, setTempPassword] = useState<string | null>(null)
  const [tempPasswordCopied, setTempPasswordCopied] = useState(false)

  useEffect(() => {
    if (open && license) {
      loadAudit(license._id)
    } else {
      setAuditLog([])
    }
    setTempPassword(null)
  }, [open, license?._id])

  const loadAudit = async (licenseId: string) => {
    setIsLoadingAudit(true)
    try {
      const response = await ApiService.getOrdoreMiniLicenseAudit(licenseId)
      if (response.success) {
        setAuditLog(response.data)
      }
    } catch {
      toast.error('Failed to load audit log')
    } finally {
      setIsLoadingAudit(false)
    }
  }

  if (!license) return null

  const handleCopyKey = () => {
    navigator.clipboard.writeText(license.licenseKey)
    setCopied(true)
    toast.success('License key copied to clipboard')
    setTimeout(() => setCopied(false), 2000)
  }

  const handleWhatsApp = () => {
    const digits = license.ownerPhone.replace(/\D/g, '')
    const text = encodeURIComponent(
      `Hi, your Ordore Mini license key is: ${license.licenseKey}. Valid for ${license.validityDays} days. Enter this key in the Ordore Mini app to activate your account.`
    )
    window.open(`https://wa.me/${digits}?text=${text}`, '_blank')
  }

  const handleResetPassword = async () => {
    setIsResettingPassword(true)
    try {
      const response = await ApiService.resetOrdoreMiniLicensePassword(license._id)
      if (response.success) {
        setTempPassword(response.data.tempPassword)
        toast.success('New temporary password generated')
        loadAudit(license._id)
      } else {
        toast.error(response.message || 'Failed to reset password')
      }
    } catch {
      toast.error('Failed to reset password')
    } finally {
      setIsResettingPassword(false)
    }
  }

  const handleCopyTempPassword = () => {
    if (!tempPassword) return
    navigator.clipboard.writeText(tempPassword)
    setTempPasswordCopied(true)
    toast.success('Temporary password copied to clipboard')
    setTimeout(() => setTempPasswordCopied(false), 2000)
  }

  const handleWhatsAppTempPassword = () => {
    if (!tempPassword) return
    const digits = license.ownerPhone.replace(/\D/g, '')
    const text = encodeURIComponent(
      `Hi, your Ordore Mini account password has been reset. Log in with phone ${license.ownerPhone} and temporary password: ${tempPassword}. Please change it after logging in.`
    )
    window.open(`https://wa.me/${digits}?text=${text}`, '_blank')
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {license.restaurantName}
            {statusBadge(license.status)}
          </DialogTitle>
          <DialogDescription>License details and activity history</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1">
          <div className="space-y-2">
            <Label>License Key</Label>
            <div className="flex items-center gap-2">
              <Input value={license.licenseKey} readOnly className="font-mono text-base tracking-wide" />
              <Button size="sm" variant="outline" onClick={handleCopyKey}>
                {copied ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button size="sm" variant="outline" onClick={handleWhatsApp}>
                <MessageCircle className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <div className="text-muted-foreground">Owner</div>
              <div className="font-medium">{license.ownerName}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Phone</div>
              <div className="font-medium">{license.ownerPhone}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Email</div>
              <div className="font-medium">{license.ownerEmail || '—'}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Validity</div>
              <div className="font-medium">{license.validityDays} days</div>
            </div>
            <div>
              <div className="text-muted-foreground">Issued</div>
              <div className="font-medium">{new Date(license.issuedAt).toLocaleDateString('en-IN')}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Expires</div>
              <div className="font-medium">{new Date(license.expiresAt).toLocaleDateString('en-IN')}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Restaurant ID</div>
              <div className="font-medium font-mono text-xs">{license.restaurantId || 'Not signed up yet'}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Last Synced</div>
              <div className="font-medium">
                {license.lastSyncedAt ? formatDistanceToNow(new Date(license.lastSyncedAt), { addSuffix: true }) : 'Never'}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Created By</div>
              <div className="font-medium">{license.createdBy}</div>
            </div>
            {license.notes && (
              <div className="col-span-2">
                <div className="text-muted-foreground">Notes</div>
                <div className="font-medium">{license.notes}</div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => onRenew(license)} className="gap-1">
              <RefreshCw className="h-4 w-4" />
              Quick Renew
            </Button>
            {license.restaurantId && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleResetPassword}
                disabled={isResettingPassword}
                className="gap-1"
              >
                {isResettingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                Reset Password
              </Button>
            )}
            {license.status !== 'REVOKED' && (
              <Button size="sm" variant="destructive" onClick={() => onRevoke(license)} className="gap-1">
                <XCircle className="h-4 w-4" />
                Revoke
              </Button>
            )}
          </div>

          {tempPassword && (
            <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
              <Label className="text-amber-900">New Temporary Password</Label>
              <p className="text-xs text-amber-800">
                Share this with the owner — it won't be shown again. They should log in (not sign up) with
                phone {license.ownerPhone} and this password.
              </p>
              <div className="flex items-center gap-2">
                <Input value={tempPassword} readOnly className="font-mono text-base tracking-wide bg-white" />
                <Button size="sm" variant="outline" onClick={handleCopyTempPassword}>
                  {tempPasswordCopied ? <CheckCircle className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button size="sm" variant="outline" onClick={handleWhatsAppTempPassword}>
                  <MessageCircle className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <History className="h-4 w-4" />
              Activity Timeline
            </div>
            {isLoadingAudit ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : auditLog.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">No activity recorded yet</div>
            ) : (
              <div className="space-y-3">
                {auditLog.map((entry) => {
                  const Icon = ACTION_ICON[entry.action] || History
                  return (
                    <div key={entry._id} className="flex items-start gap-3 text-sm">
                      <div className="mt-0.5 rounded-full bg-muted p-1.5">
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">
                          {ACTION_LABEL[entry.action] || entry.action}
                          {entry.performedBy && <span className="text-muted-foreground font-normal"> by {entry.performedBy}</span>}
                        </div>
                        {entry.note && <div className="text-muted-foreground">{entry.note}</div>}
                        <div className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true })}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
